import React, { useState, useCallback, useEffect } from 'react';
import { strings } from 'src/localization';
import { ComponentMapIds } from 'src/components/map';
import { IView } from 'Framework';
import css from './NewCustInfo.module.scss';
import { ApptCust, ApptVeh, Customer, DecodeRec, UpdateApptCustInp, UpdateApptVehInp, VehicleRec, ascFormEngagementInput } from 'src/models';
import { TextField, PhoneField, EmailField } from '@framework-controls/core';
import Footer from '../Footer/Footer';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { ICustomerState } from 'src/store/states/Customer/CustomerSlice';
import { ascFormEngagement } from 'src/helpers/EventHelper/EventHelper';
import { buildApptData, sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';
import { IAppointmentState, ISpecsState } from 'src/store/states';


const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source +
    /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

export interface IProps {
    rtsToken: string;
    customerInfo?: ICustomerState;
    custInfo?: Customer;
    custError?: string;
    profile: string;
    store: string;
    branch: string;
    gmRewardsEligible: string | undefined;
    gmMemberErr: string;
    myGMRewardsLaunched: string | undefined;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    pushToMainPanes: (inputView: IView) => void;
    updateApptCust: (inp: UpdateApptCustInp) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    checkUniqueEmail: (email: string, nameId: string, profileNum: string, store: string, branch: string,
        lastName: string, fristName: string, phone: string) => void;
    clearCustError: () => void;
    mainPaneViewOrder: IView[];
    apptCust: ApptCust
    apptVeh: ApptVeh,
    decodedData: DecodeRec,
    decodeVin: (vin: string, store: string, branch: string, checkOwned: string,
        lastName?: string, phoneNumber?: string, email?: string) => void;
    clearDecoded: () => void;
    updateApptVeh: (inp: UpdateApptVehInp) => void;
    oemFirstName: string;
    oemLastName: string;
    gmMemberSearch: (inp: string) => void;
    transFlag?: string;
    gmossVersion: string;
    locale: string;
    theme: string;
    specData: ISpecsState;
    sessionId: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    appointment: IAppointmentState;
}

const NewCustInfo = (props: IProps) => {
    const [firstName, setFirstName] = useState(props.apptCust.custFirstName);
    const [lastName, setLastName] = useState(props.apptCust.custLastName);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState({ firstName: true, lastName: true, phone: true, email: true });
    const [emailCheck, setEmailCheck] = useState(true);
    const [clearingDecoded, setClearingDecoded] = useState(false);
    const [nameLengthError, setNameLengthError] = useState(false);
    const [formChanged, setFormChanged] = useState(false);

    const custInfo = props.customerInfo?.Customer;
    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    const nameChangeHandler = useCallback((id: string, value: string) => {
        const validateFirstName = (name: string) => {
            // First and last name combined cannot be greater than 35 characters in length.
            if (name.length <= 0) {
                setError({ ...error, firstName: true });
            } else if ((name + lastName).length > 35){
                setError({ ...error, firstName: true, lastName: true });
                setNameLengthError(true);
            } else {
                setError({ ...error, firstName: false });
                setNameLengthError(false);
            }
        };
        const validateLastName = (name: string) => {
            if (name.length <= 0) {
                setError({ ...error, lastName: true });
            } else if ((name + firstName).length > 35) {
                setError({ ...error, lastName: true, firstName: true });
                setNameLengthError(true);
            } else {
                setError({ ...error, lastName: false });
                setNameLengthError(false);
            }
        };
        switch (id) {
            case 'firstName':
                setFirstName(value);
                validateFirstName(value);
                break;
            case 'lastName':
                setLastName(value);
                validateLastName(value);
                break;
            default:
                break;
        }
        if (formChanged === false) {
            setFormChanged(true);
        }
    }, [error, firstName, lastName, formChanged]);

    const phoneNumberChangedHandler = useCallback((id: string, value: string) => {
        const updatedValue = value;
        if (updatedValue === '' || !(updatedValue.length === 10)) {
            setError({ ...error, phone: true });
        } else {
            setError({ ...error, phone: false });
        }
        setPhoneNumber(updatedValue);
        if (formChanged === false) {
            setFormChanged(true);
        }
    }, [error, formChanged]);

    const emailChangedHandler = useCallback((id: string, value: string) => {
        if (!value?.match(emailRegex)) {
            setError({ ...error, email: true });
        } else {
            setError({ ...error, email: false });
        }
        setEmail(value);
        if (formChanged === false) {
            setFormChanged(true);
        }
    }, [error, formChanged]);

    useEffect(() => {
        if (formChanged) {
            sendSdEvent('scheduleServiceFormStart', buildApptData(props.sessionId,
                getProviderId(props.specData?.specs, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                    props.lexusMCLaunched, props.lexusTDMCLaunched),
                props.appointment),
            );
        }
    }, [formChanged, props.sessionId, props.specData?.specs, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
        props.lexusMCLaunched, props.lexusTDMCLaunched, props.appointment]);

    const proceedHandler = useCallback(() => {
        // If vinowned error, need to determine whether the entered information matches the
        // owner of the vin.
        props.checkUniqueEmail(email, '', props.profile, props.store, props.branch, firstName, lastName, phoneNumber);
        if (props.apptVeh.vin) {
            props.decodeVin(props.apptVeh.vin, props.store, props.branch, 'Y', lastName, phoneNumber, email);
        }
        setEmailCheck(true);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.Proceed,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [
        email,
        props.checkUniqueEmail,
        props.profile,
        props.store,
        props.branch,
        firstName,
        lastName,
        phoneNumber,
        props.apptVeh.vin,
        props.decodeVin,
        props.vehicles,
        props.selectedVehicle,
    ]);

    const onBlurHandler = useCallback((id) => {
        let elementText = '';
        switch (id) {
            case 'firstName':
                elementText = strings.FirstName;
                break;
            case 'lastName':
                elementText = strings.LastName;
                break;
            case 'phoneNumber':
                elementText = strings.Phone;
                break;
            case 'email':
                elementText = strings.EmailAddress;
                break;
            default:
                break;
        }
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: elementText,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        if (props.apptCust) {
            setPhoneNumber(props.apptCust.custPhoneNumber);
            if (!props.apptCust.custEmailAddress?.includes('*')) {
                setEmail(props.apptCust.custEmailAddress);
            }
        }
        if (props.oemFirstName) {
            setFirstName(props.oemFirstName);
        }
        if (props.oemLastName) {
            setLastName(props.oemLastName);
        }
    }, [props.apptCust, props.oemFirstName, props.oemLastName]);

    useEffect(() => {
        if (!clearingDecoded && props.apptVeh.vin) {
            setClearingDecoded(true);
            props.clearDecoded();
        }
    }, [clearingDecoded, props.clearDecoded, props.apptVeh.vin]);

    useEffect(() => {
        if (custInfo) {
            if (props.customerInfo?.sameFirstNames) {
                setFirstName(custInfo.firstName);
            }
        }
    }, [custInfo, props.customerInfo?.sameEmail,
        props.customerInfo?.sameFirstNames,
        props.customerInfo?.sameLastNames,
        props.apptCust.custEmailAddress,
    ]);

    useEffect(() => {
        if (emailCheck && props.custError==='0000') {
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.NewCustInfo) {
                props.clearCustError();
                const inp: UpdateApptCustInp = {
                    nameId: '',
                    firstName: firstName,
                    lastName: lastName,
                    emailAddress: email,
                    phoneNumber: phoneNumber,
                    phoneType: props.custInfo?.phoneType,
                    prefContact: '',
                };
                props.updateApptCust(inp);
                if (inp.emailAddress && !props.gmMemberErr && props.myGMRewardsLaunched === 'Y') {
                    props.gmMemberSearch(inp.emailAddress);
                }
                if ((props.transFlag === 'P' && props.gmossVersion === '1.4.2') || props.transFlag === 'U'){
                    props.pushToMainPanes({
                        viewId: ComponentMapIds.PickupDelivery,
                        component: ComponentMapIds.PickupDelivery,
                    });
                } else {
                    props.pushToMainPanes({
                        viewId: ComponentMapIds.Summary,
                        component: ComponentMapIds.Summary,
                    });
                }
            }
        }
    }, [emailCheck,
        props.custError,
        firstName,
        lastName,
        email,
        phoneNumber,
        props.custInfo?.phoneType,
        props.gmMemberErr,
        props.myGMRewardsLaunched,
        props.updateApptCust,
        props.pushToMainPanes,
        props.clearCustError,
        props.gmMemberSearch,
        props.mainPaneViewOrder,
        props.transFlag,
        props.gmossVersion]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'New Customer Information', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        sendSdEvent('scheduleServiceFormShown', buildApptData(props.sessionId,
            getProviderId(props.specData?.specs, props.toyotaMCLaunched, props.toyotaTDMCLaunched,
                props.lexusMCLaunched, props.lexusTDMCLaunched), props.appointment));
    }, [props.sessionId, props.specData?.specs, props.toyotaMCLaunched, props.toyotaTDMCLaunched, props.lexusMCLaunched,
        props.lexusTDMCLaunched, props.appointment]);

    const closeHandler = () => {
        props.clearCustError();
        setEmailCheck(false);
    };

    const errorMessage = () => {
        if (emailCheck && props.custError && props.custError === '1007') {
            const headerMsg = strings.EmailUsed;
            const errorMsg = strings.formatString(strings.EmailUsedMsgNoSave, email).toString();
            const primaryMessage = strings.TryAgain;
            const secondaryMessage = strings.Cancel;

            return (
                <ErrorMessage
                    headerMsg={headerMsg}
                    errorMsg={errorMsg}
                    primaryBtnMessage={primaryMessage}
                    secondaryBtnMessage={secondaryMessage}
                    primaryHandler={closeHandler}
                    secondaryHandler={closeHandler}
                    closeHandler={closeHandler}
                />
            );
        }

        return null;
    };

    const errMessage = errorMessage();

    let isDisabled = true;
    if (firstName?.length > 0 && lastName?.length > 0 && phoneNumber?.length > 0 && email?.match(emailRegex) &&
    email.length <= 62 && (firstName + lastName).length <= 35) {
    // !nameLengthError && !lastNameLengthError && email.length <= 62 && (firstName + lastName).length <= 35) {
        isDisabled = false;
    }

    return (
        <div className={css.NewCustInfo}>
            {errMessage}
            <div className={css.topDiv}>
                <div>
                    <h2 className={
                        props.theme === 'mazda' ? css.headerMazda :
                            props.theme === 'genesis' ? css.headerGenesis :
                                undefined
                    }>{strings.AlmostThere}</h2>
                    <h3>{strings.ReadyToAdd}</h3>
                    {/* <p>
                        <span>{strings.ReadyToAdd}</span>
                    </p> */}
                    <hr />
                </div>
                <div className={css.bottomContainer}>
                    <span>{strings.RequiredFields}</span>
                    <div className={css.Input}>
                        <TextField
                            id='firstName'
                            label={strings.FirstName}
                            value={firstName}
                            size='full'
                            type='text'
                            onChange={nameChangeHandler}
                            onBlur={onBlurHandler}
                            error={nameLengthError}
                            helperText={nameLengthError ? strings.ValidNameLength : ''}
                        />
                    </div>
                    <div className={css.Input}>
                        <TextField
                            id='lastName'
                            label={strings.LastName}
                            value={lastName}
                            size='full'
                            type='text'
                            onChange={nameChangeHandler}
                            onBlur={onBlurHandler}
                            error={nameLengthError}
                            helperText={nameLengthError ? strings.ValidNameLength : ''}
                        />
                    </div>
                    <div className={css.Input}>
                        <PhoneField
                            id='phoneNumber'
                            label={strings.PhoneNumber}
                            value={phoneNumber}
                            size='full'
                            onChange={phoneNumberChangedHandler}
                            onBlur={onBlurHandler}
                        />
                    </div>
                    <div className={css.Input}>
                        <EmailField
                            id='email'
                            label={strings.EmailAddress}
                            value={email}
                            size='full'
                            onChange={emailChangedHandler}
                            onBlur={onBlurHandler}
                            maxLength={62}
                        />
                    </div>
                </div>
            </div>
            <div className={css.btnDiv}>
                <Footer
                    id='NewCustInfoFooter'
                    handler={proceedHandler}
                    message={strings.Proceed}
                    secondaryhandler={proceedHandler}
                    isDisabled={isDisabled}
                    theme={props.theme}
                />
            </div>
        </div >
    );
};

export default NewCustInfo;